import React, { useMemo, useState } from 'react';
import {
  Grid,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Checkbox,
  Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { TenderTypes, Purchase, Refundable } from '../Types';
import { computeRefundDescription } from './utils';

const useStyles = makeStyles(theme => ({
  container: {
    margin: '32px 0 24px 0',
    paddingRight: 8,
    paddingTop: '8px !important',
    paddingBottom: 8,
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    '& h3': {
      marginTop: 0
    }
  },
  table: {
    width: '440px !important',
    minHeight: 32,
    border: 'none',
    margin: '2px 0 14px',
    '& tr': {
      border: 'none !important'
    }
  },
  summaryTotals: {
    borderTop: '1px solid #D1D5DB',
    paddingTop: 14,
    paddingRight: 16,
    display: 'flex',
    justifyContent: 'space-between'
  },
  tableBody: {
    border: 'none'
  },
  description: {
    display: 'flex',
    margin: '16px 0 0',
    fontSize: 18
  },
  amountCell: {
    textAlign: 'end',
    width: 100
  },
  accountBalanceCheck: {
    marginBottom: 4,
    '& .MuiCheckbox-root': {
      backgroundColor: 'initial',
      paddingLeft: 0
    }
  }
}));

interface Props {
  purchase: Purchase;
  refundables: Refundable[];
  setRefundAllToAccountBalance: (o: boolean) => void;
  refundAllToAccountBalance: boolean;
}

let i = 0;
export const RefundSummary: React.FC<Props> = ({
  refundables,
  purchase,
  setRefundAllToAccountBalance,
  refundAllToAccountBalance
}) => {
  const classes = useStyles();
  const [totalRefundDescription, setTotalRefundDescription] = useState('');
  const hasNonCreditTenders = (purchase?.payment_methods?.length ?? 0) > 0;

  useMemo(() => {
    setTotalRefundDescription(computeRefundDescription(refundables));
  }, [refundables]);

  const enableReturnToAcntBal = useMemo(
    () => refundables.some(r => r.amountToRefund > 0),
    [refundables]
  );

  return (
    <Grid item className={classes.container}>
      <Typography className={classes.description} variant="h3">
        Receipt
      </Typography>
      <Table className={classes.table}>
        <TableBody className={classes.tableBody}>
          {refundables.map(r => (
            <TableRow key={i++}>
              <TableCell>{r.description}</TableCell>
              <TableCell className={classes.amountCell}>
                {r.type === TenderTypes.CREDIT_PM || r.amountToRefund === 0
                  ? r.amountToRefund
                  : r.amountToRefund?.toFixed(2)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Grid className={classes.summaryTotals}>
        <Typography className={classes.description} variant="h3">
          Total Refund
        </Typography>
        <Typography className={classes.description} variant="h3">
          {totalRefundDescription}
        </Typography>
      </Grid>
      {hasNonCreditTenders && (
        <Grid className={classes.accountBalanceCheck}>
          <Checkbox
            onChange={() =>
              setRefundAllToAccountBalance(!refundAllToAccountBalance)
            }
            checked={refundAllToAccountBalance}
            disabled={!enableReturnToAcntBal}
          />
          <Typography variant="body4">
            Refund to account balance instead of original tender
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};
